import { matchesAllTokens, searchTokens } from '@sitzmark/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { SearchSelect } from '../../components/search-select.js';
import { Badge, Button, Callout, Field, Modal } from '../../components/ui.js';
import { api, errorMessage } from '../../lib/api.js';
import { categoryRowTint, categoryTint } from '../../lib/category-colour.js';
import type { PreviewResult, SetupData } from './wizard-machine.js';

/**
 * Filing a hundred styles, on the screen that already lists them (SCOPE §5.1,
 * §5.2; decision 214).
 *
 * Two things can be set per style here: its **gender**, from the store's one
 * list, and its **category**, where one vendor order holds more than one kind
 * of thing (jackets and helmets). Both are optional: a style left alone is
 * filed in the import's category with no gender.
 *
 * **It is a refinement, not a step.** §2.2 budgets a template import at ≤5
 * interactions and `wizard-machine.test.ts` asserts that on the reducer, so
 * anything mandatory here would break a promise that is currently kept.
 *
 * **How it selects, and why not dragging.** A drag across a scrolling table
 * fights the scroll on a touch screen, which is the counter this is heading
 * for, and it overshoots with no undo. Choose what to apply — a gender, or a
 * category — then, cheapest first:
 *
 *  - **Filter, then take everything shown.** Vendor files spell gender and
 *    garment into the style name — "M Pant", "W Jacket" — so a search is
 *    already the grouping.
 *  - **Shift-click a range**, for a run the filter cannot express.
 *  - **Click one row**, for the exception.
 *
 * Colour is the feedback (§2.2-6): a gender's chip and the rows given it wear
 * the same tint, derived from its id, so "the blue ones are Men" is checkable
 * at a glance rather than by reading a hundred cells.
 */

type Gender = SetupData['categories'][number];
type Category = SetupData['departments'][number];

/** What a click applies: a gender (or none), or a category (or the import's). */
type Brush =
  | { readonly kind: 'gender'; readonly id: string | null }
  | { readonly kind: 'category'; readonly id: string | null };

export interface CategoryAssignProps {
  readonly groups: PreviewResult['groups'];
  readonly genders: readonly Gender[];
  readonly categories: readonly Category[];
  readonly defaultDepartmentId: string | null;
  /** Gender by style, where set by hand. */
  readonly gendered: Readonly<Record<string, string>>;
  /** Category by style, where filed somewhere other than the import's. */
  readonly filed: Readonly<Record<string, string>>;
  readonly onGender: (styleKeys: readonly string[], genderId: string | null) => void;
  readonly onFile: (styleKeys: readonly string[], departmentId: string | null) => void;
}

export function CategoryAssign({
  groups,
  genders,
  categories,
  defaultDepartmentId,
  gendered,
  filed,
  onGender,
  onFile,
}: CategoryAssignProps) {
  const queryClient = useQueryClient();
  // A store whose categories say who it is for keeps no genders (decision 224):
  // then a click files models, and there is no gender to set.
  const noGenders = genders.length === 0;
  const [brush, setBrush] = useState<Brush>(
    noGenders ? { kind: 'category', id: null } : { kind: 'gender', id: genders[0]?.id ?? null },
  );
  const [term, setTerm] = useState('');
  const [anchor, setAnchor] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');

  const create = useMutation({
    mutationFn: () => api.categories.createGender.mutate({ name: newName.trim(), group: null }),
    onSuccess: async (created) => {
      // The wizard's setup feeds the chips, so it has to hear about this.
      await queryClient.invalidateQueries({ queryKey: ['import', 'setup'] });
      setBrush({ kind: 'gender', id: created.id });
      setCreating(false);
      setNewName('');
    },
  });

  /*
   * Every word matches, and they need not be adjacent — the same rule the
   * register and the product list use, from the same primitive. This is what
   * makes `kids jacket` find `KIDS FLYNN JACKET`.
   */
  const tokens = searchTokens(term);
  const shown =
    tokens.length === 0
      ? groups
      : groups.filter((group) =>
          matchesAllTokens(`${group.styleLabel} ${group.styleNumber ?? ''}`, tokens),
        );

  const genderById = new Map(genders.map((gender) => [gender.id, gender]));
  const categoryById = new Map(categories.map((category) => [category.id, category]));
  const defaultPath =
    defaultDepartmentId === null ? null : (categoryById.get(defaultDepartmentId)?.path ?? null);

  const apply = (styleKeys: readonly string[]) => {
    if (brush.kind === 'gender') onGender(styleKeys, brush.id);
    else onFile(styleKeys, brush.id);
  };

  /**
   * Click applies to one style. Shift-click applies to everything between it
   * and the last one clicked — over the *filtered* list, because that is the
   * run the operator can see and therefore the run they mean.
   */
  const clickRow = (styleKey: string, withShift: boolean): void => {
    if (withShift && anchor !== null) {
      const from = shown.findIndex((group) => group.styleKey === anchor);
      const to = shown.findIndex((group) => group.styleKey === styleKey);
      if (from !== -1 && to !== -1) {
        const [start, end] = from <= to ? [from, to] : [to, from];
        apply(shown.slice(start, end + 1).map((group) => group.styleKey));
        setAnchor(styleKey);
        return;
      }
    }
    apply([styleKey]);
    setAnchor(styleKey);
  };

  const withGender = groups.filter((group) => gendered[group.styleKey] !== undefined).length;
  const elsewhere = groups.filter((group) => filed[group.styleKey] !== undefined).length;
  const brushLabel =
    brush.kind === 'gender'
      ? brush.id === null
        ? 'no gender'
        : (genderById.get(brush.id)?.name ?? 'that gender')
      : brush.id === null
        ? 'the import’s category'
        : (categoryById.get(brush.id)?.path ?? 'that category');

  return (
    <div className="assign">
      <p className="text-secondary">
        Optional. Every new model is filed in <strong>{defaultPath ?? 'the category above'}</strong>{' '}
        {noGenders
          ? 'unless you file it somewhere else here. Pick the category, then click models — hold Shift to take a run of them.'
          : 'with no gender unless you set it here. Choose what to apply, then click models — hold Shift to take a run of them.'}
      </p>

      {noGenders ? null : (
        <div className="assign__mode" role="group" aria-label="What a click applies">
          <button
            type="button"
            className={`chip${brush.kind === 'gender' ? ' chip--on' : ''}`}
            aria-pressed={brush.kind === 'gender'}
            onClick={() => setBrush({ kind: 'gender', id: genders[0]?.id ?? null })}
          >
            Gender
          </button>
          <button
            type="button"
            className={`chip${brush.kind === 'category' ? ' chip--on' : ''}`}
            aria-pressed={brush.kind === 'category'}
            onClick={() => setBrush({ kind: 'category', id: null })}
          >
            Category
          </button>
        </div>
      )}

      {brush.kind === 'gender' ? (
        <div className="chips assign__chips">
          {genders.map((gender) => (
            <button
              key={gender.id}
              type="button"
              className={`chip category-chip${brush.id === gender.id ? ' chip--on assign__chip--active' : ''}`}
              style={categoryTint(gender.id)}
              onClick={() => setBrush({ kind: 'gender', id: gender.id })}
              aria-pressed={brush.id === gender.id}
            >
              {gender.name}
            </button>
          ))}
          <button
            type="button"
            className={`chip${brush.id === null ? ' chip--on assign__chip--active' : ''}`}
            onClick={() => setBrush({ kind: 'gender', id: null })}
            aria-pressed={brush.id === null}
          >
            No gender
          </button>
          <button type="button" className="chip" onClick={() => setCreating(true)}>
            + New gender
          </button>
        </div>
      ) : (
        <div className="field">
          <label className="field__label" htmlFor="assign-category">
            File clicked models in
          </label>
          <SearchSelect
            id="assign-category"
            options={[
              {
                value: '',
                label: `The import’s category${defaultPath === null ? '' : ` (${defaultPath})`}`,
              },
              ...categories.map((category) => ({ value: category.id, label: category.path })),
            ]}
            value={brush.id ?? ''}
            onChange={(value) => setBrush({ kind: 'category', id: value === '' ? null : value })}
            placeholder="Pick a category — type to search"
          />
        </div>
      )}

      <div className="assign__controls">
        <input
          className="input"
          placeholder="Filter models — try “pant”, “M ”, “jacket”…"
          value={term}
          onChange={(event) => {
            setTerm(event.target.value);
            // The anchor belongs to the list that was on screen when it was set.
            setAnchor(null);
          }}
        />
        <Button
          variant="secondary"
          disabledReason={shown.length === 0 ? 'Nothing matches that filter' : undefined}
          onClick={() => apply(shown.map((group) => group.styleKey))}
        >
          {tokens.length === 0
            ? `Give all ${groups.length} ${brushLabel}`
            : `Give these ${shown.length} ${brushLabel}`}
        </Button>
      </div>

      <div className="table-scroll">
        <table className="table">
          <thead>
            <tr>
              <th>Model</th>
              <th>Category</th>
              {noGenders ? null : <th>Gender</th>}
              <th>Model #</th>
              <th className="num">Variants</th>
              <th className="num">Units</th>
              <th>What happens</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((group) => {
              const genderId = gendered[group.styleKey];
              const gender = genderId === undefined ? undefined : genderById.get(genderId);
              const categoryId = filed[group.styleKey];
              const category = categoryId === undefined ? undefined : categoryById.get(categoryId);

              return (
                <tr
                  key={group.styleKey}
                  className="assign__row"
                  style={genderId === undefined ? undefined : categoryRowTint(genderId)}
                  onClick={(event) => clickRow(group.styleKey, event.shiftKey)}
                >
                  <td>{group.styleLabel}</td>
                  <td>
                    {category === undefined ? (
                      <span className="text-secondary">
                        {group.isNewStyle ? 'The import’s' : '—'}
                      </span>
                    ) : (
                      category.path
                    )}
                  </td>
                  {noGenders ? null : (
                    <td>
                      {gender === undefined ? (
                        <span className="text-secondary">—</span>
                      ) : (
                        <span className="chip category-chip" style={categoryTint(gender.id)}>
                          {gender.name}
                        </span>
                      )}
                    </td>
                  )}
                  <td className="code">{group.styleNumber ?? '—'}</td>
                  <td className="num">{group.variantCount}</td>
                  <td className="num">{group.totalQuantity}</td>
                  <td>
                    {group.isNewStyle ? (
                      <Badge tone="success">New model</Badge>
                    ) : group.newVariantCount > 0 ? (
                      <Badge tone="info">{group.newVariantCount} new variants</Badge>
                    ) : (
                      <Badge tone="neutral">Update</Badge>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/*
        §2.2-10: the figure that tells you where you are, rather than leaving
        it to be counted off the screen.
      */}
      <p className="text-secondary assign__summary">
        {`${withGender} of ${groups.length} with a gender; ${elsewhere} filed somewhere other than the import’s category.`}
        {tokens.length === 0 ? null : ` Showing ${shown.length}.`}
      </p>

      <Modal
        open={creating}
        title="New gender"
        description="It joins the store's one list of genders, ready to use straight away. Give it a group in Inventory → Categories."
        onClose={() => {
          setCreating(false);
          setNewName('');
          create.reset();
        }}
        primaryAction={
          <Button
            variant="primary"
            busy={create.isPending}
            disabledReason={newName.trim() === '' ? 'A name is needed' : undefined}
            onClick={() => create.mutate()}
          >
            Create
          </Button>
        }
        secondaryAction={
          <Button
            variant="ghost"
            onClick={() => {
              setCreating(false);
              setNewName('');
              create.reset();
            }}
          >
            Cancel
          </Button>
        }
      >
        <Field label="Name" hint="As staff say it — Men, Lady, Junior Boy.">
          {(props) => (
            <input
              {...props}
              className="input"
              autoFocus
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
            />
          )}
        </Field>
        {create.isError ? <Callout tone="danger">{errorMessage(create.error)}</Callout> : null}
      </Modal>
    </div>
  );
}
